import React from 'react';
import { Currency } from '@common/components';
import { toUSFormat } from 'works/legacy/lib/date-helper';
import { StatusPill, TextCapitalized } from '@institutions/components';
import { RatesLongTermUpdatesDeviationTable } from './_rates-long-term-step-updates-deviation-table';
import {
  usePendingStaffingRequestsDeviation,
  useRateCardCreationAttributes,
} from './_rates-long-term-context';
import { BillingCardPendingStaffingRequestType } from '@institutions/graphql';
import { ShiftType } from '@common/graphql';
import { getBillingCardRateByShiftType } from '@institutions/services';

export function RatesLongTermStepUpdatesTabPendingStaffingRequest({
  dataSource,
}: {
  dataSource?: BillingCardPendingStaffingRequestType[];
}) {
  const [rateCardAttributes] = useRateCardCreationAttributes();

  const [pendingStaffingRequests, { setAll, deviate, undeviate }] =
    usePendingStaffingRequestsDeviation();
  const tableColumns = [
    {
      dataIndex: 'code',
      key: 'id',
      title: 'Id',
    },
    {
      dataIndex: ['organizationUnit', 'name'],
      key: 'unitName',
      title: 'Unit Name',
    },
    {
      dataIndex: ['organizationUnit', 'costCenter'],
      key: 'costCenter',
      title: 'Cost Center',
    },
    {
      dataIndex: ['organizationBusiness', 'name'],
      key: 'facility',
      title: 'Facility',
    },
    {
      dataIndex: 'shiftType',
      key: 'shiftType',
      title: 'Shift Type',
      render: (shiftType: string) => <TextCapitalized text={shiftType} />,
    },
    {
      dataIndex: 'startDate',
      key: 'startDate',
      title: 'Start Date',
      render: toUSFormat,
    },
    {
      dataIndex: 'staffingRequest',
      key: 'currentRate',
      title: 'Current Rate',
      render: (_: unknown, record: BillingCardPendingStaffingRequestType) => {
        const highest = record.highestRate ?? 0;
        const lowest = record.lowestRate ?? 0;

        if (highest !== lowest) {
          return (
            <>
              <Currency value={lowest} /> - <Currency value={highest} />
            </>
          );
        }

        return <Currency value={lowest} />;
      },
    },
    {
      dataIndex: 'shiftType',
      key: 'newRate',
      title: 'New Rate',
      render: (
        shiftType: ShiftType,
        record: BillingCardPendingStaffingRequestType
      ) => {
        const billingCardRateByShiftType = getBillingCardRateByShiftType(
          rateCardAttributes,
          shiftType
        );

        if (!billingCardRateByShiftType) {
          return <Currency value={record.billingRegularRate} />;
        }

        const rates = Object.values(billingCardRateByShiftType);
        const lowestCost = Math.min(...rates);
        const highestCost = Math.max(...rates);

        if (highestCost !== lowestCost) {
          return (
            <>
              <Currency value={lowestCost} /> - <Currency value={highestCost} />
            </>
          );
        }

        return <Currency value={lowestCost} />;
      },
    },
    {
      dataIndex: 'state',
      key: 'status',
      title: 'Status',
      render: (status: string) => <StatusPill status={status} />,
    },
  ];

  return (
    <RatesLongTermUpdatesDeviationTable
      dataSource={dataSource}
      columns={tableColumns}
      onDeviateList={setAll}
      onDeviateItem={deviate}
      onUndeviateItem={undeviate}
      deviatedIds={pendingStaffingRequests}
    />
  );
}
